import React, { FC, ReactText, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../../rootReducer';
import { AppDispatch } from '../../../store';
import {
  getTurbinesInfo,
  TurbineInfo,
  SequenceInfo,
  getSequencesInfo,
  searchTurbines,
  getAssignees,
  bulkTurbinePublish,
  bulkTurbineUpdate,
  setShowBulkUpdateModal,
  setClearSelection,
  setPublishError,
  setAllHiddenColumns,
  setBulkUpdateIssues,
  BulkTurbinesUpdateConfig,
  PublishRequest,
  publishTurbines,
  SearchFields
} from './Landing.slice';
import { message, Modal, Table } from 'antd';
import { prepareTurbineGridDataSelector } from './turbineGridSelector';
import { whereToNavigate, whatKeysToSave } from './Helpers';
import SearchForm from './SearchForm/SearchForm';
import { getAnnotationStandardDetails } from '../../AnnotationManager/Annotation/annotation.slice';
import { SelectOption } from '../../AnnotationManager/Annotation/annotation.types';
import { TableRowSelection } from 'antd/lib/table/interface';
import BulkUpdateModal from './BulkUpdate/BulkUpdateModal';
import { Permissions } from '../../../shared/models/permissions.enum';
import { UseHaveAnyPermissions } from '../../../Auth/UseHaveAnyPermissions';
import PublishInfo from './PublishInfo/PublishInfo';
import { MessageType } from 'antd/lib/message';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import TableOptions from '../TableOptions/index';
import { adjustColumns, Column, ColumnProps } from '../TableOptions/Helpers';
import { getSequenceColumns } from './Helpers';
import ResolutionModal from './ResolutionModal';
import PublishModal from './PublishModal';

export type StoreProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const Landing: FC<RouteComponentProps & StoreProps> = ({
  location,
  currentPage,
  totalNumber,
  gridData,
  isLoading,
  size,
  sites,
  inspections,
  turbines,
  statuses,
  severities,
  selectedSites,
  selectedInspections,
  selectedTurbines,
  selectedStatuses,
  selectedSeverities,
  selectedFindingIDs,
  assignees,
  isPublishing,
  showBulkUpdateModal,
  clearSelection,
  publishError,
  updateIssues,
  standard,
  searchTurbines,
  getTurbineInfos,
  getSequences,
  getAsignees,
  onBulkTurbinePublish,
  onBulkTurbinesUpdate,
  onSetShowBulkUpdateModal,
  onSetClearSelection,
  onClearPublishError,
  onColumnsSelected,
  clearBulkUpdateIssues,
  onGetAnnotationStandardDetails,
  onConditionalTurbinesPublish
}) => {
  const { annotationManagerMode } = whereToNavigate(location);

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedRowsInspections, setSelectedRowsInspections] = useState<string[]>([]);
  const [resolution, setResolution] = useState('');
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  const canReassign = UseHaveAnyPermissions([Permissions.AnnotationManager_AssignTurbine, Permissions.AnnotationManager_UnassignTurbine]);

  useEffect(() => {
    getTurbineInfos(1, size, true);
  }, [getTurbineInfos, size]);

  useEffect(() => {
    let loadingSpinner: MessageType | null = null;
    if (isPublishing) {
      loadingSpinner = message.loading('Publish of selected turbines is in progress..', 0);
    }
    return () => {
      if (loadingSpinner) {
        loadingSpinner();
      }
    };
  }, [isPublishing]);

  useEffect(() => {
    if (canReassign) {
      getAsignees();
    }
  }, [canReassign, getAsignees, gridData.dataSource.length]);

  useEffect(() => {
    if (clearSelection) {
      setSelectedRowKeys([]);
      onSetClearSelection(false);
    }
  }, [clearSelection, onSetClearSelection]);

  const onSelectRow = (selectedRowKeys: ReactText[], rowsData: (TurbineInfo & { key: string })[]) => {
    let selectedInspections: string[] = rowsData.map(r => r.inspection);
    setSelectedRowsInspections(selectedInspections);
    setSelectedRowKeys(selectedRowKeys as string[]);
  };

  const onShowBulk = () => {
    onSetShowBulkUpdateModal(true);
  };

  const getSequenceTableContent = (sequences: SequenceInfo[]) => {
    if (!sequences || (sequences && sequences.length === 0)) {
      return {
        isLoading: true,
        columns: [],
        dataSource: []
      };
    }

    let columns = getSequenceColumns();

    const dataSource = sequences.map(x => ({
      ...x,
      key: `${x.blade}/${x.bladeSide}`
    }));

    return {
      isLoading: false,
      columns,
      dataSource
    };
  };

  const expandedRowRender = (turbineInfo: TurbineInfo) => {
    const sequencesGridData = getSequenceTableContent(turbineInfo.sequences);
    return (
      <div>
        <Table
          style={{ width: '700px' }}
          loading={sequencesGridData.isLoading}
          columns={sequencesGridData.columns}
          dataSource={sequencesGridData.dataSource}
          pagination={false}
        />
      </div>
    );
  };

  const onExpand = (expanded: boolean, record: TurbineInfo & { key: string }) => {
    if (!expanded) {
      setExpandedKeys([...expandedKeys.filter(x => x !== record.key)]);
      return;
    }
    if (!record.sequences) {
      getSequences(record.site, record.inspection, record.turbine);
    }
    setExpandedKeys([...expandedKeys, record.key]);
  };

  const onRefresh = () => {
    getTurbineInfos(currentPage, size, false);
    setExpandedKeys([]);
  };

  const onPublishTurbines = () => {
    onBulkTurbinePublish(selectedRowKeys, resolution);
    setShowResolutionModal(false);
  };

  const onPublish = () => {
    let inspectionsSet = new Set(selectedRowsInspections);
    if (inspectionsSet.size > 1) {
      Modal.warning({
        title: 'Warning',
        content: 'Bulk publish requires selected turbines to belong to only one inspection'
      });
      return;
    }

    setShowPublishModal(true);
  };

  const preparedSites = sites.map(x => ({ text: x, value: x }));
  const preparedInspections = inspections.map(x => ({ text: x, value: x }));
  const preparedTurbines = turbines.map(x => ({ text: x, value: x }));
  const preparedStatuses = statuses.map(x => ({ text: x, value: x }));
  const preparedSeverities = severities.map(x => ({ text: x, value: x }));

  const searchFormParams = {
    isLoading,
    sites: preparedSites,
    inspections: preparedInspections,
    turbines: preparedTurbines,
    findingTypes: [],
    assessors: [],
    selectedSites,
    selectedInspections,
    selectedTurbines,
    selectedStatuses,
    selectedSeverities,
    selectedFindingIDs,
    selectedFindingTypes: [],
    selectedAssessors: [],
    statuses: preparedStatuses,
    severities: preparedSeverities,
    showAdvanced: false,
    filtersIndicator: false,
    showBulk: selectedRowKeys.length > 0,
    onShowBulk,
    searchTurbines,
    onRefresh,
    onPublish,
    showFindingIdSearch: false
  };

  const rowSelection: TableRowSelection<
    TurbineInfo & {
      key: string;
    }
  > = {
    selectedRowKeys,
    onChange: onSelectRow,
    hideSelectAll: false
  };

  var assigneesOptions: SelectOption[] = assignees.map(x => ({ text: x.fullName, value: x.id }));

  const footer = () => `Total rows: ${totalNumber}`;

  const iconsConfig = { columnSelector: true };

  return (
    <div style={{ padding: '10px 50px 50px 50px' }}>
      <div style={{ paddingBottom: '15px' }}>
        <h4>{annotationManagerMode ? 'Annotation Module' : 'Stitching Tool'}</h4>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <SearchForm {...searchFormParams} />
      </div>
      <TableOptions
        columns={adjustColumns(gridData).columns}
        onColumnsSelected={onColumnsSelected}
        iconsConfig={iconsConfig}
        savedKeys={whatKeysToSave()}
      />
      <Table
        style={{ minHeight: '320px', height: '100%' }}
        className='landing-table'
        columns={adjustColumns(gridData).allVisibleColumns}
        dataSource={gridData.dataSource}
        loading={isLoading}
        scroll={{ y: 500, x: gridData.columns.filter((x: ColumnProps) => x.isVisible).length * 220 }}
        expandedRowRender={expandedRowRender}
        onExpand={onExpand}
        expandedRowKeys={expandedKeys}
        pagination={{
          current: currentPage,
          total: totalNumber,
          pageSize: size,
          onChange: (page: number) => {
            getTurbineInfos(page, size);
          },
          onShowSizeChange: (current: number, size: number) => {
            getTurbineInfos(1, size);
          }
        }}
        rowSelection={rowSelection}
        footer={footer}
      />

      <BulkUpdateModal
        showModal={showBulkUpdateModal}
        assignees={assigneesOptions}
        selectedRowKeys={selectedRowKeys}
        updateIssues={updateIssues}
        onCancel={() => onSetShowBulkUpdateModal(false)}
        bulkUpdate={onBulkTurbinesUpdate}
        clearBulkUpdateIssues={clearBulkUpdateIssues}
      />
      {publishError && <PublishInfo message={publishError} showModal onCancel={onClearPublishError} />}
      <ResolutionModal
        showModal={showResolutionModal}
        resolution={resolution}
        publishing={isPublishing}
        setResolution={setResolution}
        onCancel={() => setShowResolutionModal(false)}
        onOk={onPublishTurbines}
      />
      <PublishModal
        showModal={showPublishModal}
        standard={standard}
        selectedTurbineIds={selectedRowKeys}
        data={gridData.dataSource}
        isPublishing={isPublishing}
        onHideModal={() => setShowPublishModal(false)}
        getTurbinesStandard={onGetAnnotationStandardDetails}
        onSubmit={onConditionalTurbinesPublish}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  gridData: prepareTurbineGridDataSelector(state),
  currentPage: state.landing.currentPage,
  totalNumber: state.landing.totalNumberOfTurbines,
  isLoading: state.landing.isLoading,
  size: state.landing.size,
  statuses: state.landing.statuses,
  sites: state.landing.sites,
  severities: state.landing.severities,
  inspections: state.landing.inspections,
  turbines: state.landing.turbines,
  selectedSites: state.landing.selectedValues.selectedSites,
  selectedInspections: state.landing.selectedValues.selectedInspections,
  selectedTurbines: state.landing.selectedValues.selectedTurbines,
  selectedStatuses: state.landing.selectedValues.selectedStatuses,
  selectedSeverities: state.landing.selectedValues.selectedSeverities,
  selectedFindingIDs: state.landing.selectedValues.selectedFindingIDs,
  assignees: state.landing.assignees,
  isPublishing: state.landing.isPublishing,
  showBulkUpdateModal: state.landing.showBulkUpdateModal,
  clearSelection: state.landing.clearSelection,
  publishError: state.landing.publishError,
  updateIssues: state.landing.bulkUpdateIssues,
  standard: state.annotation.annotationStandardDetails
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getTurbineInfos: (newPage: number, size: number, init: boolean = false) => dispatch(getTurbinesInfo(init, size, newPage)),
  getSequences: (site: string, inspection: string, turbine: string) => dispatch(getSequencesInfo(site, inspection, turbine)),
  searchTurbines: (fields: SearchFields) => dispatch(searchTurbines(fields)),
  getAsignees: () => dispatch(getAssignees()),
  onBulkTurbinePublish: (turbineIds: string[], resolution: string) => dispatch(bulkTurbinePublish(turbineIds, resolution)),
  onBulkTurbinesUpdate: (model: BulkTurbinesUpdateConfig) => dispatch(bulkTurbineUpdate(model)),
  onSetShowBulkUpdateModal: (show: boolean) => dispatch(setShowBulkUpdateModal(show)),
  onSetClearSelection: (clear: boolean) => dispatch(setClearSelection(clear)),
  onClearPublishError: () => dispatch(setPublishError(null)),
  onColumnsSelected: (toBeHidden: string[]) => dispatch(setAllHiddenColumns(toBeHidden)),
  clearBulkUpdateIssues: () => dispatch(setBulkUpdateIssues([])),
  onGetAnnotationStandardDetails: (id: string) => dispatch(getAnnotationStandardDetails(id)),
  onConditionalTurbinesPublish: (request: PublishRequest) => dispatch(publishTurbines(request))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Landing));
